import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import TimeSheet from "./TimeSheet";
import { fetchAllTimeEntries, updateMyTimeLog } from "../../Redux/Thunks/timeEntryThunk";
import {
  selectAllTimeEntriesData,
  selectAllTimeEntriesLoading,
  selectAllTimeEntriesError,
  selectMyTimeLogSaving,
} from "../../Redux/Slices/timeEntrySlice";
import { fetchTeamsData } from "../../Redux/Thunks/teamsThunk";
import { fetchUsersData } from "../../Redux/Thunks/usersThunk";
import { fetchClientsData } from "../../Redux/Thunks/clientsThunk";
import { fetchProjectsData } from "../../Redux/Thunks/projectManagementThunk";
import { selectTeamsData } from "../../Redux/Slices/teamsSlice";
import { selectUsersData } from "../../Redux/Slices/usersSlice";
import { selectClientsData } from "../../Redux/Slices/clientsSlice";
import { selectProjectsListForDropdown } from "../../Redux/Slices/projectsManagementSlice";
import { getListFromApiResponse } from "../utils/apiListMappers";
import { mapProjectsToFilterOptions } from "../Projects/projectMappers";
import { formatDate, formatDateRange, getTodayDateRange } from "../../utils/dateUtils";
import {
  buildAllTimeLogsExportQueryParams,
  buildAllTimeLogsFetchParams,
} from "../../utils/timeEntryQueryParams";
import { downloadAllTimeLogsExport } from "../../utils/downloadTimeLogsExport";
import { showSuccess, showError } from "../../utils/toast";
import { isSilentRequestAbortError } from "../../utils/apiErrorMessage";
import { buildUserDropdownOptionsFromUsersData } from "../../utils/userFilterOptions";
import {
  LIST_PAGE_SIZE_OPTIONS,
  readStoredListPageSize,
  writeStoredListPageSize,
  resolveListTotalPages,
  TIME_ENTRY_ALL_LIST_PAGE_SIZE_STORAGE_KEY,
} from "../../Utility/Pagination/listPaginationConstants";
import { resolveTimesheetSummaryFromMetaOrRows } from "./timeEntryDisplayUtils";

const INITIAL_FILTERS = {
  client: [],
  project: [],
  team: [],
  user: [],
};

export default function AllTimeEntries() {
  const dispatch = useDispatch();
  const data = useSelector(selectAllTimeEntriesData);
  const loading = useSelector(selectAllTimeEntriesLoading);
  const error = useSelector(selectAllTimeEntriesError);
  const saving = useSelector(selectMyTimeLogSaving);
  const teamsData = useSelector(selectTeamsData);
  const usersData = useSelector(selectUsersData);
  const clientsData = useSelector(selectClientsData);
  const projectsData = useSelector(selectProjectsListForDropdown);

  const [currentDate] = useState(() => new Date());
  const [dateRange, setDateRange] = useState(() => getTodayDateRange());
  const [dateRangeDraft, setDateRangeDraft] = useState(() => getTodayDateRange());
  const [filterDraft, setFilterDraft] = useState(() => ({ ...INITIAL_FILTERS }));
  const [appliedFilters, setAppliedFilters] = useState(() => ({ ...INITIAL_FILTERS }));
  const [filterExpanded, setFilterExpanded] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() =>
    readStoredListPageSize(TIME_ENTRY_ALL_LIST_PAGE_SIZE_STORAGE_KEY)
  );
  const [newestFirst, setNewestFirst] = useState(false);

  useEffect(() => {
    if (!teamsData) dispatch(fetchTeamsData({ getAll: true }));
    if (!usersData) dispatch(fetchUsersData({ getAll: true }));
    if (!clientsData) dispatch(fetchClientsData({ getAll: true }));
    if (!projectsData) dispatch(fetchProjectsData({ getAll: true }));
  }, [dispatch, teamsData, usersData, clientsData, projectsData]);

  const fetchParams = useMemo(
    () =>
      buildAllTimeLogsFetchParams({
        currentDate,
        timesheetDateRange: dateRange,
        appliedFilters,
        projectsData,
        clientsData,
        teamsData,
        page,
        limit: pageSize,
        newestFirst,
      }),
    [
      currentDate,
      dateRange,
      appliedFilters,
      projectsData,
      clientsData,
      teamsData,
      page,
      pageSize,
      newestFirst,
    ]
  );

  useEffect(() => {
    dispatch(fetchAllTimeEntries(fetchParams));
  }, [dispatch, fetchParams]);

  const refetch = useCallback(() => {
    dispatch(fetchAllTimeEntries(fetchParams));
  }, [dispatch, fetchParams]);

  const filterOptions = useMemo(() => {
    const clients = getListFromApiResponse(clientsData)
      .map((c) => c.clientName ?? c.name ?? "")
      .filter(Boolean);
    const teams = getListFromApiResponse(teamsData)
      .map((t) => t.teamName ?? t.name ?? "")
      .filter(Boolean);
    const users = buildUserDropdownOptionsFromUsersData(usersData);

    return {
      clients: [...new Set(clients)].sort(),
      projects: mapProjectsToFilterOptions(projectsData),
      teams: [...new Set(teams)].sort(),
      users,
      tasks: [],
    };
  }, [clientsData, projectsData, teamsData, usersData]);

  const dateRangeLabel = useMemo(() => {
    const { startDate, endDate } = dateRange ?? {};
    if (startDate && endDate) {
      if (startDate === endDate) return formatDate(startDate);
      return formatDateRange(startDate, endDate);
    }
    return formatDate(currentDate);
  }, [dateRange, currentDate]);

  const groups = useMemo(() => {
    const list = data?.list;
    if (!Array.isArray(list) || list.length === 0) return [];

    const byDate = {};
    list.forEach((entry) => {
      const dateLabel = entry.day || entry.dateLabel || "Timesheet";
      if (!byDate[dateLabel]) {
        byDate[dateLabel] = { dateLabel, entries: [] };
      }
      byDate[dateLabel].entries.push(entry);
    });

    return Object.values(byDate);
  }, [data?.list]);

  const summary = useMemo(
    () => resolveTimesheetSummaryFromMetaOrRows(data?.meta, groups),
    [data?.meta, groups]
  );

  const totalPages = useMemo(
    () => resolveListTotalPages(data?.meta, pageSize, data?.list?.length ?? 0),
    [data?.meta, pageSize, data?.list?.length]
  );

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilterDraft((prev) => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    setAppliedFilters({ ...filterDraft });
    setDateRange(dateRangeDraft);
    setFilterExpanded(true);
    setPage(1);
  };

  const handleFilterReset = () => {
    const todayRange = getTodayDateRange();
    setFilterDraft({ ...INITIAL_FILTERS });
    setAppliedFilters({ ...INITIAL_FILTERS });
    setDateRange(todayRange);
    setDateRangeDraft(todayRange);
    setPage(1);
  };

  const exportQueryParams = useMemo(
    () =>
      buildAllTimeLogsExportQueryParams({
        currentDate,
        timesheetDateRange: dateRange,
        appliedFilters,
        projectsData,
        clientsData,
        teamsData,
        newestFirst,
      }),
    [currentDate, dateRange, appliedFilters, projectsData, clientsData, teamsData, newestFirst]
  );

  const handleNewestFirstChange = useCallback((checked) => {
    setNewestFirst(!!checked);
    setPage(1);
  }, []);

  const handleExportCsv = useCallback(async () => {
    try {
      await downloadAllTimeLogsExport(exportQueryParams, "csv");
      showSuccess("CSV export downloaded.");
    } catch (e) {
      if (isSilentRequestAbortError(e)) return;
      showError(typeof e === "string" ? e : e?.message || "Export failed.");
    }
  }, [exportQueryParams]);

  const handleExportXlsx = useCallback(async () => {
    try {
      await downloadAllTimeLogsExport(exportQueryParams, "xlsx");
      showSuccess("All time entries export downloaded.");
    } catch (e) {
      if (isSilentRequestAbortError(e)) return;
      showError(typeof e === "string" ? e : e?.message || "Export failed.");
    }
  }, [exportQueryParams]);

  return (
    <TimeSheet
      loading={loading}
      error={error}
      groups={groups}
      filters={filterDraft}
      onFilterChange={handleFilterChange}
      filterOptions={filterOptions}
      summary={summary}
      dateRangeLabel={dateRangeLabel}
      applyFilters={applyFilters}
      dateRangeDraft={dateRangeDraft}
      onDateRangeDraftChange={setDateRangeDraft}
      handleFilterReset={handleFilterReset}
      filterExpanded={filterExpanded}
      onFilterExpandedChange={setFilterExpanded}
      showTeam={true}
      showUser={true}
      showTask={false}
      showLogTime={false}
      showExport={true}
      showDeleteAction={false}
      onExportCsv={handleExportCsv}
      onExportXlsx={handleExportXlsx}
      showNewestFirstToggle={true}
      newestFirst={newestFirst}
      onNewestFirstChange={handleNewestFirstChange}
      preserveApiRowOrder={true}
      saving={saving}
      onRefetchTimeEntries={refetch}
      onEditEntry={async ({ id, minutesLogged, logDate, comment }) => {
        if (!id) return;
        await dispatch(updateMyTimeLog({ id, minutesLogged, logDate, comment })).unwrap();
        await refetch();
      }}
      pagination={{
        currentPage: page,
        totalPages,
        pageSize,
        onPageChange: setPage,
        onPageSizeChange: (next) => {
          writeStoredListPageSize(TIME_ENTRY_ALL_LIST_PAGE_SIZE_STORAGE_KEY, next);
          setPageSize(next);
          setPage(1);
        },
        pageSizeOptions: LIST_PAGE_SIZE_OPTIONS,
      }}
    />
  );
}
